
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import TailwindLayout from 'TailwindcssComponents/TailwindLayout';
import TopBar from 'TailwindcssComponents/TopBar';
import DataTable from 'TailwindcssComponents/Tables';
import axios from 'axios';
import FooterTailwind from 'TailwindcssComponents/Footer';
import DeleteDialog from 'components/DeleteDialogBox/deleteDialog';
import { ToastContainer, toast } from 'react-toastify';
import { UserDataContext } from 'context';
import defaultLogo from 'assets/images/logos/defaultlogo.png';
import 'react-toastify/dist/ReactToastify.css';

export default function Events() {
  const apiUrl = process.env.REACT_APP_API_BASE_URL;
  const bearerToken = localStorage.getItem('token');
  const { userData, selectedOrganization } = useContext(UserDataContext);
  const userRole = userData?.isAdmin ? 'admin' : selectedOrganization?.name || 'user';
  const orgId = selectedOrganization?.organization?.id || null;
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [eventsData, setEventsData] = useState([]);
  const [openDeleteDialogBox, setOpenDeleteDialogBox] = useState();
  const [selectedEventId, seSelectedEventId] = useState(null);
  const [totalItemsCount, setTotalItemsCount] = useState(0);
  const [totalPagesCount, setTotalPagesCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [eventStatus, setEventStatus] = useState(null);
  const [eventCategories, setEventCategories] = useState(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [selectedCategoryName, setSelectedCategoryName] = useState();
  const [selectedDate, setSelectedDate] = useState(null);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchEvents = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${apiUrl}event-service/event`, { params: { page: currentPage, limit: rowsPerPage, orgId: orgId, status: eventStatus, category: selectedCategoryId, date: selectedDate, name: searchTerm } });
      if (response.data?.success) {
        const sortedEvents = [...response.data.data].sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
        setEventsData(sortedEvents);
        setFilteredEvents(sortedEvents);
        setTotalItemsCount(response.data.totalItems || 0);
        setTotalPagesCount(response.data.totalPages || 0);
      } else throw new Error(response.data?.message || 'Error in fetching events');
    } catch (error) {
      console.error(error.response?.data?.message || error.message);
    }
    finally {
      setIsLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${apiUrl}event-service/event-category`);
      if (response.data?.success) {
        setEventCategories(response.data.data);
      } else throw new Error(response.data?.message || 'Error in fetching category');
    } catch (error) {
      console.error(error.response?.data?.message || error.message);
    }
  };

  const handleActionClick = (id, action) => {
    if (action === 'view') {
      navigate(`/events/view-events/single-event/${id}`);
    }
    else if (action === 'edit') {
      navigate(`/events/edit-event/${id}`);
    }
    else if (action === 'delete') {
      handleDelete(id);
    }
  };

  const handleDelete = async (id) => {
    seSelectedEventId(id);
    setOpenDeleteDialogBox(true);
  };

  const closeDeleteDialogBox = () => {
    setOpenDeleteDialogBox(false);
  };

  const handleDeleteConfirm = async () => {
    try {
      setIsLoading(true);
      const response = await axios.delete(`${apiUrl}event-service/event/${selectedEventId}`,
        {
          headers: { 'Authorization': `Bearer ${bearerToken}` }
        });
      if (response?.data.success) {
        toast.warning(response?.data.message || 'Event has been deleted');
      }
      else throw new Error(response?.data.message || 'Unable to delete');
    } catch (error) {
      console.error(error.response?.data.message || error.message);
      toast.error(error.response?.data.message || error.message);
    } finally {
      fetchEvents();
      setOpenDeleteDialogBox(false);
      setIsLoading(false);
    }
  };

  const filterEvents = (term) => {
    if (!term) {
      setFilteredEvents(eventsData); // Reset to all data if search term is empty
      return;
    }
    const lowerCaseTerm = term.toLowerCase();
    const filtered = eventsData.filter((event) =>
      event.name.toLowerCase().includes(lowerCaseTerm) || // Filter by name
      event.description?.toLowerCase().includes(lowerCaseTerm) // Optional: Filter by description
    );
    setFilteredEvents(filtered);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    filterEvents(term);
  };

  useEffect(() => {
    fetchEvents();
    fetchCategories();
    if (selectedCategoryId && eventCategories) {
      const selectedCategory = eventCategories?.find((category) => category.id === selectedCategoryId);
      setSelectedCategoryName(selectedCategory ? selectedCategory.name : null);
    }
  }, [currentPage, rowsPerPage, eventStatus, selectedCategoryId, selectedDate, searchTerm, bearerToken]);

  const formattedDate = (date) => {
    const options = { day: '2-digit', month: 'short', year: 'numeric' };
    return new Date(date).toLocaleDateString('en-GB', options);
  };

  const currentDate = new Date();

  const getRegistrationStatus = (registrationOpenDate, isRegistrationOpen, registrationCloseDate) => {
    const registrationDate = new Date(registrationOpenDate);
    const timeDiff = registrationDate - currentDate;
    const daysDiff = timeDiff / (1000 * 60 * 60 * 24); // Convert milliseconds to days

    if (isRegistrationOpen) {
      return 'Open';
    } else if (daysDiff > 0 && daysDiff <= 7 && !isRegistrationOpen) {
      return 'Opening Soon';
    } else if (!isRegistrationOpen || registrationCloseDate < currentDate) {
      return 'Closed';
    }
  };

  const columns = [
    {
      key: 'name', label: 'Event Name',
      render: (row) => (
        <div className="flex items-center gap-3">
          <img
            src={row.coverImage?.secure_url || defaultLogo}
            alt={`${row.name} cover`}
            className="w-10 h-10 rounded-full object-cover"
          />
          <span>{row.name}</span>
        </div>
      ),
    },
    {
      key: 'orgId',
      label: 'Event Organization',
      render: (row) => (row.organization?.name)
    },
    { key: 'createdAt', label: 'Created Date', render: (row) => formattedDate(row.createdAt) },
    { key: 'start', label: 'Event Date', render: (row) => formattedDate(row.eventDateTimes[0].date) },
    {
      key: 'status',
      label: 'Status',
      render: (row) => (
        <div
          className={`border rounded ${getStatusColor(row.status)} w-fit h-7 flex items-center`}
        >
          <div className="py-1 px-3">{row.status.charAt(0).toUpperCase() + row.status.slice(1).toLowerCase()}</div>
        </div>
      ),
    },
    {
      key: 'registrationStatus',
      label: 'Registration Status',
      render: (row) => {
        const regStatus = getRegistrationStatus(row.registrationOpenDate, row.isRegistrationOpen, row.registrationCloseDate);
        return (
          <div className={`border rounded ${getStatusColor(regStatus)} w-fit h-7 flex items-center`}>
            <div className="py-1 px-3">{regStatus}</div>
          </div>
        );
      },
    },
  ];

  const getStatusColor = (status) => {
    const statusColors = {
      active: 'bg-[#45B369] bg-opacity-15 text-[#45B369] border-[#45B369]',
      published: 'bg-[#F86624] bg-opacity-15 text-[#EF4A00] border-[#EF4A00]',
      draft: 'bg-red-100 text-red-700 border-red-500',
      Open: 'bg-green-100 text-green-700 border-green-500',
      'Opening Soon': 'bg-yellow-100 text-yellow-700 border-yellow-500',
      Closed: 'bg-red-100 text-red-700 border-red-500',
    };
    return statusColors[status] || 'bg-gray-100 text-gray-700';
  };

  return (
    <TailwindLayout>
      <TopBar />
      <div className=" p-12 bg-gray-100 min-h-screen">
        <DataTable
          title={'Events'}
          rows={filteredEvents}
          columns={columns}
          onActionClick={handleActionClick}
          options={{ showHeader: true, showHeaderTwo: false, viewOption: true, editOption: true, addoption: false, statusFilterOption: true, dateFilterOption: true, typeFilterOption: true, searchOption: true }}
          totalCount={totalItemsCount}
          pagesCount={totalPagesCount}
          currentPage={currentPage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(limit) => setRowsPerPage(limit)}
          onPageChange={(page) => setCurrentPage(page)}
          eventStatus={eventStatus}
          onStatusChange={(status) => setEventStatus(status)}
          categories={eventCategories}
          onCategoryChange={(id) => setSelectedCategoryId(id)}
          category={selectedCategoryName}
          onDateChange={(date) => setSelectedDate(date)}
          filteredDate={selectedDate}
          onSearch={handleSearch}
          searcItem={searchTerm}
          role={userRole}
          loaderStatus={isLoading}
          addButtonOnlyOption={true}
          addButtonTitle={"Add Event"}
          handleDialogBoxes={() => navigate("/events/add-eventNew")}
        />
      </div>
      <DeleteDialog
        open={openDeleteDialogBox}
        onClose={closeDeleteDialogBox}
        onDelete={handleDeleteConfirm}
        name={'event'}
      />
      <ToastContainer
        position="bottom-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <FooterTailwind />
    </TailwindLayout>
  )
}